import { useState, useEffect } from 'react';

/**
 * 디바이스 레이아웃 타입
 * 화면 비율(height/width)을 기반으로 결정
 */
export type DeviceLayout = 'phone' | 'fold' | 'desktop';

/**
 * 비율 기반 레이아웃 감지 훅
 *
 * | 디바이스 | 비율 (height/width) | 레이아웃 |
 * |----------|---------------------|----------|
 * | 일반폰 | > 1.5 (세로로 좁음) | phone |
 * | 폴드폰/태블릿 | 1.0 ~ 1.5 (정사각에 가까움) | fold |
 * | PC | < 1.0 (가로로 넓음) | desktop |
 */
export function useDeviceLayout(): DeviceLayout {
  const [layout, setLayout] = useState<DeviceLayout>(() => {
    // 서버 사이드에서는 기본값 반환
    if (typeof window === 'undefined') {
      return 'desktop';
    }
    return calculateLayout();
  });

  useEffect(() => {
    const updateLayout = () => {
      const newLayout = calculateLayout();
      setLayout(newLayout);
    };

    // 초기 계산
    updateLayout();

    // 리사이즈 이벤트 리스너
    window.addEventListener('resize', updateLayout);

    // 화면 회전 감지 (모바일)
    window.addEventListener('orientationchange', updateLayout);

    return () => {
      window.removeEventListener('resize', updateLayout);
      window.removeEventListener('orientationchange', updateLayout);
    };
  }, []);

  return layout;
}

/**
 * PC 플랫폼 여부 확인
 *
 * 판별 우선순위:
 * 1. userAgentData (Chrome 90+): platform + mobile 조합으로 정확히 구분
 *    - Windows/macOS/Linux/ChromeOS → PC (터치스크린이 있어도)
 *    - Android + mobile=false (Galaxy Fold 펼침) → 모바일 (비율 기반)
 * 2. UA 파싱 fallback: OS 키워드로 데스크톱 OS 여부 확인
 * 3. 터치 없음 → PC
 *
 * '데스크톱 사이트 요청' 모드에서 Android가 desktop UA를 보내면
 * desktop으로 판별되는데, 사용자가 의도한 동작이므로 허용한다.
 */
function isPCPlatform(): boolean {
  // 1. userAgentData가 있으면 platform으로 OS 판별 (가장 신뢰)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const userAgentData = (navigator as any).userAgentData;
  if (userAgentData) {
    const platform = (userAgentData.platform || '').toLowerCase();
    const desktopPlatforms = ['windows', 'macos', 'linux', 'chromeos'];
    if (desktopPlatforms.some((p) => platform.includes(p))) {
      return true; // 데스크톱 OS → 터치 여부와 무관하게 PC
    }
    // platform이 **비어 있으면** 모바일 단정 금지 — UA 오버라이드 잔재(devtools/자동화
    // 에뮬레이션)나 프라이버시 설정이 platform만 지울 수 있고, 그때 여기서 false를
    // 반환하면 터치스크린 PC가 화면비만으로 phone이 된다(2026-08-02 실사고: 에뮬레이션
    // 해제 후에도 platform=""가 남아 데스크톱 전폭에 하단 네비가 렌더됨). 아래 UA
    // 문자열 판별로 내려보낸다.
    if (platform) {
      // Android/iOS 등 모바일 OS가 명시된 경우만 모바일로 단정
      return false;
    }
  }

  // 2. userAgentData 미지원: UA 문자열로 판별
  const ua = navigator.userAgent.toLowerCase();

  // 데스크톱 OS 키워드가 있으면 PC (터치 PC 포함)
  if (ua.includes('windows') || ua.includes('macintosh') || ua.includes('cros')) {
    return true;
  }

  // 모바일 키워드가 있으면 모바일
  const mobileKeywords = ['android', 'iphone', 'ipad', 'ipod', 'mobile'];
  if (mobileKeywords.some((keyword) => ua.includes(keyword))) {
    return false;
  }

  // 3. 판별 불가: 터치 없으면 PC, 있으면 모바일
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    return false;
  }
  return true;
}

/**
 * 현재 화면 비율로 레이아웃 계산
 * - PC 플랫폼(터치 없음)은 항상 desktop 레이아웃
 * - 터치 디바이스는 screen의 긴 변/짧은 변 비율로 폼팩터 판별
 *
 * 터치 디바이스에서 window.innerHeight는 브라우저 주소창·상태바에 의해
 * 줄어들고, screen.width/height는 화면 회전에 따라 바뀌므로
 * 긴 변/짧은 변(orientation-independent) 비율을 사용한다.
 *
 * | 디바이스 | 긴/짧 비율 | 레이아웃 |
 * |----------|-----------|----------|
 * | 일반폰   | ~2.2      | phone    |
 * | 폴드 펼침 | ~1.25     | fold     |
 * | iPad     | ~1.33     | fold     |
 * | Surface  | ~1.50     | fold     |
 */
function calculateLayout(): DeviceLayout {
  // PC 플랫폼은 창 크기와 관계없이 desktop 레이아웃
  if (isPCPlatform()) {
    return 'desktop';
  }

  // 터치 디바이스: 긴 변/짧은 변 비율로 폼팩터 판별
  // (회전·브라우저 크롬과 무관하게 디바이스 고유 형태를 반영)
  const longSide = Math.max(screen.width, screen.height);
  const shortSide = Math.min(screen.width, screen.height);
  const ratio = longSide / shortSide;

  if (ratio > 1.5) {
    return 'phone'; // 일반폰 (세로로 길고 좁음)
  } else {
    return 'fold'; // 폴드폰/태블릿 (정사각에 가까움)
  }
}

/**
 * 레이아웃별 추가 정보를 포함한 훅
 */
export function useDeviceLayoutDetails() {
  const layout = useDeviceLayout();
  const [details, setDetails] = useState({
    layout,
    ratio: 0,
    width: 0,
    height: 0,
    isTouchDevice: false,
  });

  useEffect(() => {
    const updateDetails = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const ratio = height / width;
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

      setDetails({
        layout: calculateLayout(),
        ratio,
        width,
        height,
        isTouchDevice,
      });
    };

    updateDetails();

    window.addEventListener('resize', updateDetails);
    window.addEventListener('orientationchange', updateDetails);

    return () => {
      window.removeEventListener('resize', updateDetails);
      window.removeEventListener('orientationchange', updateDetails);
    };
  }, []);

  return details;
}

export default useDeviceLayout;
